import { useEffect, useState } from "react";
import Lenis from "lenis";
import {
  motion,
  useScroll,
  useMotionValueEvent,
  AnimatePresence,
} from "motion/react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import BackgroundBlob from "./components/BackgroundBlob";
import Navbar from "./components/Navbar";
import Technology from "./components/Technology";
import Home from "./pages/Home";
import Work from "./components/Work";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import ServicePage from "./pages/ServicePage";
import CategoryPage from "./pages/CategoryPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import ContactModal from "./components/ContactModal";

import Footer from "./components/Footer";

export default function App() {
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("theme");
        return saved ? saved === "dark" : true;
      } catch (e) {
        return true;
      }
    }
    return true;
  });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();

  const pageTitles: Record<string, string> = {
    "/": "Innovative technology solutions, immersive experiences, and cutting-edge digital services for modern businesses.",
    "/about": "About",
    "/technology": "Services",
    "/work": "Work",
    "/blog": "Blog",
  };

  useEffect(() => {
    const base = pageTitles[location.pathname];
    if (base) {
      document.title = `Kivas - ${base}`;
    } else if (location.pathname.startsWith("/services/")) {
      const serviceMap: Record<string, string> = {
        "3d-animation": "Animation Studio",
        "ar-vr": "Technology",
        hardware: "Hardware Solutions",
      };
      const id = location.pathname.split("/").pop() || "";
      document.title = `Kivas - ${serviceMap[id] || "Services"}`;
    } else if (location.pathname.startsWith("/technology/category/")) {
      const slugMap: Record<string, string> = {
        "ar-experiences": "AR Experiences",
        "vr-experiences": "VR Experiences",
        "ai-innovations": "AI Innovations",
        "immersive-technology": "Immersive Technology",
        "projection-animation": "Projection & Animation",
        "software-technology": "Software Technology",
      };
      const slug = location.pathname.split("/").pop() || "";
      document.title = `Kivas - ${slugMap[slug] || "Category"}`;
    } else {
      document.title = "Kivas";
    }
  }, [location]);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("theme-dark");
      root.style.setProperty("color-scheme", "dark");
    } else {
      root.classList.remove("theme-dark");
      root.style.setProperty("color-scheme", "light");
    }
    try {
      localStorage.setItem("theme", isDark ? "dark" : "light");
    } catch (e) {
      console.warn("localStorage is not available");
    }
  }, [isDark]);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 500) {
      setIsScrollTopVisible(true);
    } else {
      setIsScrollTopVisible(false);
    }
  });

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    (window as any).lenis = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href^="#"]') as HTMLAnchorElement;
      if (link) {
        const href = link.getAttribute("href");
        if (href && href.startsWith("#")) {
          e.preventDefault();
          const id = href.slice(1);
          const element = document.getElementById(id);
          if (element) {
            lenis.scrollTo(element, { offset: -80 });
          }
        }
      }
    };

    document.addEventListener("click", handleAnchorClick);

    return () => {
      document.removeEventListener("click", handleAnchorClick);
      lenis.destroy();
      delete (window as any).lenis;
    };
  }, []);

  useEffect(() => {
    const lenisInstance = (window as any).lenis;
    if (isContactModalOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      if (lenisInstance) lenisInstance.stop();
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (lenisInstance) lenisInstance.start();
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (lenisInstance) lenisInstance.start();
    };
  }, [isContactModalOpen]);

  return (
    <div className="bg-[#fcfcfc] dark:bg-[#050505] min-h-screen text-black dark:text-white selection:bg-indigo-500 selection:text-white transition-colors duration-500">
      {location.pathname === "/" && <BackgroundBlob isDark={isDark} />}

      <Navbar
        isDark={isDark}
        setIsDark={setIsDark}
        setIsContactModalOpen={setIsContactModalOpen}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      <Routes>
        <Route
          path="/"
          element={<Home setIsContactModalOpen={setIsContactModalOpen} />}
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/technology" element={<Technology />} />
        <Route path="/technology/category/:slug" element={<CategoryPage />} />
        <Route path="/work" element={<Work />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogDetailPage />} />
        <Route path="/services/:id" element={<ServicePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>

      <Footer setIsContactModalOpen={setIsContactModalOpen} />

      <AnimatePresence>
        {isScrollTopVisible && !isSidebarOpen && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-black dark:bg-white text-white dark:text-black rounded-full flex items-center justify-center hover:opacity-90 active:scale-[0.97] transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] shadow-2xl interactive"
            aria-label="Scroll to top"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m18 15-6-6-6 6" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}
